import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Percent,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  updateDoc,
  doc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';
import AddDiscountForm from '../../components/admin/AddDiscountForm';

const DiscountsManagementNewFixed = () => {
  const navigate = useNavigate();
  
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      console.log('🔄 Fetching discounts...');
      const q = query(collection(db, 'discounts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const discountsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDiscounts(discountsData);
      console.log('✅ Fetched discounts:', discountsData.length);
    } catch (error) {
      console.error('❌ Error fetching discounts:', error);
      toast.error('Failed to fetch discounts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (discount) => {
    console.log('📝 Editing discount:', discount);
    setEditingDiscount(discount);
    setShowAddForm(true);
  };

  const handleDelete = async (discountId) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      try {
        await deleteDoc(doc(db, 'discounts', discountId));
        toast.success('Discount deleted successfully!');
        fetchDiscounts();
      } catch (error) {
        console.error('Error deleting discount:', error);
        toast.error('Failed to delete discount');
      }
    }
  };

  const toggleDiscountStatus = async (discountId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'discounts', discountId), {
        isActive: !currentStatus,
        updatedAt: new Date()
      });
      
      setDiscounts(prevDiscounts =>
        prevDiscounts.map(discount =>
          discount.id === discountId ? { ...discount, isActive: !currentStatus } : discount
        )
      );
      
      toast.success(`Discount ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating discount status:', error);
      toast.error('Failed to update discount status');
    }
  };

  const handleFormSuccess = () => {
    console.log('🎉 Form submission successful, refreshing discounts...');
    fetchDiscounts();
  };

  const handleFormClose = () => {
    console.log('🚪 Closing form...');
    setShowAddForm(false);
    setEditingDiscount(null);
  };

  const getStatusColor = (discount) => {
    if (!discount.isActive) return '#dc2626';
    
    const now = new Date();
    const validFrom = new Date(discount.validFrom);
    const validUntil = new Date(discount.validUntil);
    
    if (now < validFrom) return '#f59e0b'; // Not started
    if (now > validUntil) return '#dc2626'; // Expired
    return '#059669'; // Active
  };

  const getStatusText = (discount) => {
    if (!discount.isActive) return 'Inactive';
    
    const now = new Date();
    const validFrom = new Date(discount.validFrom);
    const validUntil = new Date(discount.validUntil);
    
    if (now < validFrom) return 'Not Started';
    if (now > validUntil) return 'Expired';
    return 'Active';
  };

  const DiscountCard = React.memo(({ discount }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '16px'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            {discount.name}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 12px 0',
            lineHeight: '1.4'
          }}>
            {discount.description}
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <span style={{
              backgroundColor: `${getStatusColor(discount)}20`,
              color: getStatusColor(discount),
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {getStatusText(discount)}
            </span>
            <span style={{
              backgroundColor: discount.discountType === 'percentage' ? '#dbeafe' : '#fef3c7',
              color: discount.discountType === 'percentage' ? '#1e40af' : '#d97706',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {discount.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
            </span>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={() => toggleDiscountStatus(discount.id, discount.isActive)}
            style={{
              padding: '8px',
              backgroundColor: discount.isActive ? '#fee2e2' : '#dcfce7',
              color: discount.isActive ? '#dc2626' : '#059669',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {discount.isActive ? <XCircle style={{ width: '16px', height: '16px' }} /> : <CheckCircle style={{ width: '16px', height: '16px' }} />}
          </button>
          <button
            onClick={() => handleEdit(discount)}
            style={{
              padding: '8px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Edit style={{ width: '16px', height: '16px' }} />
          </button>
          <button
            onClick={() => handleDelete(discount.id)}
            style={{
              padding: '8px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Percent style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>
              {discount.discountType === 'percentage' ? 'Discount' : 'Amount'}
            </p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {discount.discountType === 'percentage' ? `${discount.discountValue}%` : `₹${discount.discountValue}`}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <DollarSign style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Min Order</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              ₹{discount.minOrderAmount}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Calendar style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Valid Until</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {new Date(discount.validUntil).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Users style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Usage Limit</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {discount.usageLimit || 'Unlimited'}
            </p>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <Clock style={{ width: '14px', height: '14px' }} />
          <span>Created: {new Date(discount.createdAt?.toDate?.() || discount.createdAt).toLocaleDateString()}</span>
        </div>
        {discount.maxDiscountAmount > 0 && (
          <span style={{
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '600'
          }}>
            Max: ₹{discount.maxDiscountAmount}
          </span>
        )}
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading discounts...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Discounts Management</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage discount offers</p>
              </div>
            </div>
            
            <Button
              onClick={() => {
                console.log('➕ Opening add discount form...');
                setEditingDiscount(null);
                setShowAddForm(true);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus style={{ width: '16px', height: '16px' }} />
              Add New Discount
            </Button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
              {discounts.length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Total Discounts</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#059669', margin: '0 0 4px 0' }}>
              {discounts.filter(d => d.isActive).length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Active Discounts</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626', margin: '0 0 4px 0' }}>
              {discounts.filter(d => !d.isActive).length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Inactive Discounts</p>
          </div>
        </div>

        {/* Discounts Grid */}
        <div style={{
          display: 'grid',
          gap: '20px'
        }}>
          {discounts.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px'
              }}>
                🎯
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                No discounts found
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Add your first discount offer to get started
              </p>
            </div>
          ) : (
            discounts.map((discount) => (
              <DiscountCard key={discount.id} discount={discount} />
            ))
          )}
        </div>
      </div>

      {showAddForm && (
        <AddDiscountForm
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          editingDiscount={editingDiscount}
        />
      )}
    </div>
  );
};

export default DiscountsManagementNewFixed;
